import { Alert, Button, Group, Modal, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { csrfHeaders, useAuth } from '../lib/auth-context';
import {
  fetchHostVerifications,
  summariseHostVerificationState,
} from '../lib/host-verification';

/** 1.6-stroke check, house style (matches inbox SearchIcon). Decorative. */
function CheckIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M5 12l4 4 10-11" />
    </svg>
  );
}

/** 1.6-stroke x, house style. Decorative. */
function XIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

/**
 * ExchangeActions — the canonical "what can I do with this exchange right
 * now" surface. Used by:
 *   - /exchanges/:id (full page, `compact={false}`)
 *   - /inbox/:threadId ThreadView (action strip under the title, `compact`)
 *
 * The two surfaces share one action vocabulary so a user never sees a button
 * on one page and not the other for the same (state, role) combination.
 *
 * `onAfterAction` is the parent's refetch hook — both surfaces invalidate
 * their own queries on a successful transition. The ThreadDO `state_changed`
 * fanout also nudges both, but the immediate call gives the user a snappier
 * post-action UI.
 */

export type ExchangeActionsViewer = {
  role: 'artist' | 'host' | 'admin';
};

export type ExchangeActionsExchange = {
  id: string;
  state:
    | 'drafted'
    | 'submitted'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'declined'
    | 'disputed'
    | 'closed';
};

export type ExchangeActionsProps = {
  exchange: ExchangeActionsExchange;
  viewer: ExchangeActionsViewer;
  artistDisplayName: string;
  counterpartyName: string;
  compact?: boolean;
  onAfterAction?: () => void | Promise<void>;
};

export function ExchangeActions({
  exchange,
  viewer,
  artistDisplayName,
  counterpartyName,
  compact = false,
  onAfterAction,
}: ExchangeActionsProps) {
  const [actioning, setActioning] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelOpen, { open: openCancel, close: closeCancel }] = useDisclosure(false);
  const [declineOpen, { open: openDecline, close: closeDecline }] = useDisclosure(false);
  const [cancelReason, setCancelReason] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [declineShare, setDeclineShare] = useState(false);

  // The host's own identity-verification gate (mirrors CFR-011 on the venue
  // edit page). This ONLY covers the host's own missing verification — the
  // artist-side identity gap (legal name / verified mobile / emergency
  // contact) has no client-side signal here and still falls through to the
  // server's 422 on confirm, which now also notifies the artist (AUD-005/015).
  const isHostConfirmable = viewer.role === 'host' && exchange.state === 'submitted';
  // /api/verification/me/verification is HAT-scoped while viewer.role is
  // PARTY-scoped: a dual-role user wearing the artist hat gets no host_user
  // rows back, which must not read as "unverified". Only run (and trust) the
  // check under a hat whose flow includes the caller's own host_user rows.
  const { activeHat } = useAuth();
  const hatShowsHostRows = activeHat === 'host' || activeHat === 'admin';
  const hostVerificationQ = useQuery({
    queryKey: ['verifications', 'me', activeHat],
    queryFn: fetchHostVerifications,
    enabled: isHostConfirmable && hatShowsHostRows,
  });
  const hostVerificationState =
    hostVerificationQ.data && hostVerificationQ.data.ok
      ? summariseHostVerificationState(hostVerificationQ.data.verifications)
      : 'none';
  // Fail open while the check is loading (or errored), and under a hat that
  // can't see host rows — the server remains the source of truth either way.
  const hostVerificationBlocksConfirm =
    isHostConfirmable &&
    hatShowsHostRows &&
    hostVerificationQ.isSuccess &&
    hostVerificationState !== 'approved';
  const hostVerificationTooltip =
    hostVerificationState === 'pending'
      ? 'Your identity verification is in review — confirming unlocks once it\'s approved.'
      : 'Verify your identity first — confirming unlocks once it\'s approved.';

  // Returns true on a successful transition, false on any failure. Callers
  // (the cancel/decline modals) rely on this so they only close and wipe the
  // typed reason on success — a failed action keeps the modal + reason so the
  // user doesn't have to retype it (FR-137).
  async function performAction(
    label: string,
    path: string,
    body: Record<string, unknown>,
  ): Promise<boolean> {
    setActioning(label);
    setActionError(null);
    try {
      const res = await fetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify(body),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message?: string } };
      if (!res.ok || !json.ok) {
        setActionError(json.error?.message ?? "Couldn't apply that change.");
        setActioning(null);
        return false;
      }
      notifications.show({
        title: `${label} done`,
        message: "We've let the other party know.",
      });
      if (onAfterAction !== undefined) await onAfterAction();
      return true;
    } catch {
      setActionError('Network problem. Try again.');
      return false;
    } finally {
      setActioning(null);
    }
  }

  // Buttons rendered for this (state, role) combo. An empty set means the
  // strip vanishes (compact) or shows just a closed-state nudge (full).
  const buttons: React.ReactNode[] = [];

  if (viewer.role === 'host' && exchange.state === 'submitted') {
    buttons.push(
      hostVerificationBlocksConfirm ? (
        <Tooltip key="confirm" label={hostVerificationTooltip} multiline w={240} withArrow>
          {/* Mantine disabled buttons don't emit pointer events, so the
              tooltip trigger needs a wrapping element that does. */}
          <span>
            <Button
              size={compact ? 'xs' : 'sm'}
              variant="filled"
              color="brand.6"
              leftSection={<CheckIcon />}
              disabled
            >
              Confirm
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          key="confirm"
          size={compact ? 'xs' : 'sm'}
          variant="filled"
          color="brand.6"
          leftSection={<CheckIcon />}
          onClick={() =>
            performAction(
              'Confirm',
              `/api/exchanges/${encodeURIComponent(exchange.id)}/confirm`,
              { exchange_id: exchange.id },
            )
          }
          loading={actioning === 'Confirm'}
        >
          Confirm
        </Button>
      ),
      <Button
        key="decline"
        size={compact ? 'xs' : 'sm'}
        variant="default"
        leftSection={<XIcon />}
        onClick={openDecline}
      >
        Decline
      </Button>,
      <Button
        key="host-submitted-cancel"
        size={compact ? 'xs' : 'sm'}
        variant="subtle"
        color="gray"
        onClick={openCancel}
      >
        Cancel
      </Button>,
    );
    if (hostVerificationBlocksConfirm) {
      buttons.push(
        <Button
          key="verify-link"
          component={Link}
          to="/verification"
          size={compact ? 'xs' : 'sm'}
          variant="subtle"
          color="brand"
        >
          Verify your identity
        </Button>,
      );
    }
  }

  if (
    viewer.role === 'artist' &&
    (exchange.state === 'submitted' || exchange.state === 'confirmed')
  ) {
    buttons.push(
      <Button
        key="artist-cancel"
        size={compact ? 'xs' : 'sm'}
        variant="default"
        color="gray"
        onClick={openCancel}
      >
        Cancel exchange
      </Button>,
    );
  }
  if (viewer.role === 'host' && exchange.state === 'confirmed') {
    buttons.push(
      <Button
        key="host-confirmed-cancel"
        size={compact ? 'xs' : 'sm'}
        variant="default"
        color="gray"
        onClick={openCancel}
      >
        Cancel exchange
      </Button>,
    );
  }

  if (
    exchange.state === 'confirmed' &&
    (viewer.role === 'artist' || viewer.role === 'host')
  ) {
    buttons.push(
      <Button
        key="mark-complete"
        size={compact ? 'xs' : 'sm'}
        variant="light"
        color="brand"
        leftSection={<CheckIcon />}
        onClick={() =>
          performAction(
            'Mark complete',
            `/api/exchanges/${encodeURIComponent(exchange.id)}/complete`,
            { exchange_id: exchange.id },
          )
        }
        loading={actioning === 'Mark complete'}
      >
        Mark complete
      </Button>,
    );
  }

  const hasButtons = buttons.length > 0;
  const hasError = actionError !== null;

  if (!hasButtons && !hasError) return null;

  return (
    <>
      {hasError ? (
        <Alert color="red" mb={compact ? 'xs' : 'sm'}>
          {actionError}
        </Alert>
      ) : null}
      {hasButtons ? (
        <Group gap={compact ? 'xs' : 'sm'} wrap="wrap">
          {buttons}
        </Group>
      ) : null}

      <Modal
        opened={cancelOpen}
        onClose={closeCancel}
        title="Cancel this exchange?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">{counterpartyName} will be notified. Tell them why:</Text>
          <Textarea
            autosize
            minRows={3}
            maxLength={2000}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
            placeholder="A short note helps the other party."
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeCancel}>
              Go back
            </Button>
            <Button
              color="red"
              disabled={cancelReason.trim().length === 0}
              loading={actioning === 'Cancel'}
              onClick={async () => {
                const ok = await performAction(
                  'Cancel',
                  `/api/exchanges/${encodeURIComponent(exchange.id)}/cancel`,
                  { exchange_id: exchange.id, reason: cancelReason.trim() },
                );
                if (!ok) return;
                closeCancel();
                setCancelReason('');
              }}
            >
              Cancel exchange
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={declineOpen}
        onClose={closeDecline}
        title="Decline this request?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            We'll let {artistDisplayName} know. You can choose whether to share your reason.
          </Text>
          <Textarea
            label="Reason (optional)"
            autosize
            minRows={3}
            maxLength={2000}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.currentTarget.value)}
            placeholder="A brief reason. They read every reply."
          />
          <Group justify="space-between" align="center">
            <label style={{ fontSize: 14 }}>
              <input
                type="checkbox"
                checked={declineShare}
                onChange={(e) => setDeclineShare(e.currentTarget.checked)}
                style={{ marginRight: 8 }}
              />
              Share my reason with {artistDisplayName}
            </label>
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={closeDecline}>
              Go back
            </Button>
            <Button
              loading={actioning === 'Decline'}
              onClick={async () => {
                const ok = await performAction(
                  'Decline',
                  `/api/exchanges/${encodeURIComponent(exchange.id)}/decline`,
                  {
                    exchange_id: exchange.id,
                    reason:
                      declineReason.trim().length > 0 ? declineReason.trim() : undefined,
                    share_with_artist: declineShare,
                  },
                );
                if (!ok) return;
                closeDecline();
                setDeclineReason('');
                setDeclineShare(false);
              }}
            >
              Decline
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
